import { useState } from "react";
import { AlertTriangle, Scale, ThumbsUp, ThumbsDown, Clock, Coins } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { api, errMsg, titleize } from "../../lib/api";

const FIT = { strong: "bg-emerald-100 text-emerald-800", possible: "bg-blue-100 text-blue-800", less_likely: "bg-slate-200 text-slate-600" };
const List = ({ icon: Icon, title, items, cls }) => !!items?.length && (
  <div><p className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${cls}`}><Icon className="h-3.5 w-3.5" />{title}</p>
    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-slate-700">{items.map((i) => <li key={i}>{i}</li>)}</ul></div>
);

const OptionCard = ({ o, onPick }) => (
  <div data-testid={`option-card-${o.code}`} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="font-display text-lg font-bold">{o.title}</h3>
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold uppercase ${FIT[o.fit]}`}>{titleize(o.fit)} fit</span>
    </div>
    <p className="mt-1 text-sm text-slate-600"><b>Goal:</b> {o.goal}</p>
    {!!o.fit_reasons.length && <p className="mt-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">{o.fit_reasons.join(" ")}</p>}
    <div className="mt-3 space-y-3">
      <List icon={ThumbsUp} title="Potential advantages" items={o.advantages} cls="text-emerald-700" />
      <List icon={ThumbsDown} title="Potential disadvantages" items={o.disadvantages} cls="text-red-700" />
      <List icon={Scale} title="Requirements" items={o.requirements} cls="text-slate-600" />
    </div>
    <div className="mt-3 grid gap-1 text-sm text-slate-700">
      <p className="flex gap-1.5"><Coins className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{o.costs}</p>
      <p className="flex gap-1.5"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{o.risks}</p>
      <p className="flex gap-1.5"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{o.timing}</p>
    </div>
    {o.conflict_of_interest && <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs font-semibold text-amber-900" data-testid={`coi-badge-${o.code}`}>Conflict-of-interest disclosure applies</p>}
    <button data-testid={`option-explore-${o.code}`} onClick={() => onPick(o)} className="tap mt-4 h-11 rounded-xl border-2 border-slate-900 font-bold uppercase tracking-wide hover:bg-slate-900 hover:text-white">I'd like to explore this path</button>
  </div>
);

export const OptionsCompare = ({ analysis, onChange }) => {
  const [pick, setPick] = useState(null);
  const [ack, setAck] = useState(false);
  const save = async (o) => {
    try {
      await api.put("/case/answer", { key: "interested_option", value: { code: o.code, coi_acknowledged: !!o.conflict_of_interest }, confidence: "reported" });
      toast.success("Noted. A specialist will walk you through this path — you can still change your mind.");
      setPick(null); setAck(false); onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const choose = (o) => (o.conflict_of_interest ? setPick(o) : save(o));
  return (
    <div data-testid="options-compare">
      <p className="mb-4 text-sm text-slate-600">These are potential paths, ordered by how well they fit <b>your stated goal, deadline, equity and situation</b> — never by what earns anyone the most. Not a recommendation or legal/financial advice.</p>
      <div className="grid gap-4 md:grid-cols-2">{analysis.solutions.map((o) => <OptionCard key={o.code} o={o} onPick={choose} />)}</div>
      <Dialog open={!!pick} onOpenChange={(v) => !v && setPick(null)}>
        <DialogContent data-testid="coi-dialog">
          <DialogHeader><DialogTitle className="font-display">Before you choose: conflict-of-interest disclosure</DialogTitle></DialogHeader>
          <p className="text-sm leading-relaxed text-slate-700">{pick?.conflict_disclosure}</p>
          <label className="flex items-center gap-3 rounded-xl bg-amber-50 p-3 text-sm font-semibold"><Checkbox data-testid="coi-ack-checkbox" checked={ack} onCheckedChange={(v) => setAck(!!v)} /> I have read and understand this disclosure.</label>
          <button data-testid="coi-continue-btn" disabled={!ack} onClick={() => save(pick)} className="tap h-12 rounded-xl bg-slate-900 font-bold uppercase text-white disabled:opacity-40">Continue with this path</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
